<template>
  <a-data-table
    :headers="headers"
    :items="documents"
    :items-per-page="10"
    style="width: 100%"
    :footer-props="{
      'items-per-page-text': $t('lblRowsPerPage'),
    }"
    sort-by="fileName"
    :sort-desc="false"
    :must-sort="true"
  >
    <template v-slot:[`item.fileName`]="{ item }">
      <v-icon v-if="!item.fileName.split('.').pop()"> mdi-file </v-icon>
      <v-icon v-else>
        {{ fileIcons[item.fileName.split(".").pop()] || "mdi-file" }}
      </v-icon>
      <v-tooltip
        bottom
        :disabled="
          !item.metadata_storage_size && !item.metadata_storage_last_modified
        "
      >
        <template v-slot:activator="{ on, attrs }">
          <a
            class="ml-3"
            style="font-weight: 700"
            v-if="$security.checkUserAccess('DownloadDocument')"
            v-bind="attrs"
            v-on="on"
            @click="onFileDownload(item)"
            >{{ item.fileName }}</a
          >
          <span v-else>{{ item.fileName }}</span>
        </template>
        <span
          v-if="
            item.metadata_storage_size && item.metadata_storage_last_modified
          "
          >{{ $t("documents.size") }}:
          {{ formatBytes(item.metadata_storage_size) }}
          {{ $t("search.uploadedDate") }}:
          {{
            formatDate(
              new Date(item.metadata_storage_last_modified),
              "MM/dd/yyyy h:mm aaa"
            )
          }}</span
        >
      </v-tooltip>
    </template>
    <template v-slot:[`item.metadata_storage_size`]="{ item }">
      {{ formatBytes(item.metadata_storage_size) }}
    </template>
    <template v-slot:[`item.metadata_storage_last_modified`]="{ item }">
      {{
        item.metadata_storage_last_modified
          ? formatDate(
              new Date(item.metadata_storage_last_modified),
              "MM/dd/yyyy h:mm aaa"
            )
          : "-"
      }}
    </template>
    <template v-slot:[`item.agendaid`]="{ item }">
      <span v-if="!item.agendaid">-</span>
      <span style="color: #333333" v-else-if="getAgendaName(item.agendaid)">{{
        getAgendaName(item.agendaid)
      }}</span>
      <v-chip v-else x-small color="error" outlined>
        {{ $t("lblDeleted") }}
      </v-chip>
    </template>
    <template v-slot:[`item.meetingid`]="{ item }">
      <!-- {{ getMeetingName(item.meetingid) }} -->
      <span v-if="!item.meetingid">-</span>
      <router-link
        v-else-if="getMeetingName(item.meetingid)"
        style="cursor: pointer; display: inline-flex"
        :to="`/meeting/${item.meetingid}/search/tenant/${tenantId}`"
      >
        {{ getMeetingName(item.meetingid) }}
      </router-link>
      <v-chip v-else x-small color="error" outlined>
        {{ $t("lblDeleted") }}
      </v-chip>
    </template>
    <template v-slot:[`item.directoryid`]="{ item }">
      <div style="color: #333333">{{ item.directoryid }}</div>
    </template>
    <template v-slot:[`item.metadata_author`]="{ item }">
      <div style="color: #333333" v-if="item.metadata_author">
        {{ item.metadata_author }}
      </div>
      <div v-else>-</div>
    </template>
  </a-data-table>
</template>

<script>
import {
  currentDateTime,
  formatBytes,
  formatDate,
  openDocument,
} from "@/utils/util";
import { mapActions, mapState, mapGetters } from "vuex";

export default {
  name: "DocumentSearch",
  props: {
    items: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      fileIcons: {
        zip: "mdi-folder-zip-outline",
        rar: "mdi-folder-zip-outline",
        htm: "mdi-language-html5",
        html: "mdi-language-html5",
        js: "mdi-nodejs",
        json: "mdi-file-document-outline",
        md: "mdi-markdown",
        pdf: "mdi-file-pdf-box",
        png: "mdi-file-image",
        jpg: "mdi-file-image",
        jpeg: "mdi-file-image",
        mp4: "mdi-filmstrip",
        mkv: "mdi-filmstrip",
        avi: "mdi-filmstrip",
        wmv: "mdi-filmstrip",
        mov: "mdi-filmstrip",
        txt: "mdi-file-document-outline",
        xls: "mdi-file-excel",
        xlsx: "mdi-file-excel",
        other: "mdi-file-outline",
      },
    };
  },
  computed: {
    ...mapState({
      agendaListData: (state) => state.agenda.agendaList,
      meetingListData: (state) => state.meeting.meetingsList,
      documentsList: (state) => state.document.documentsList,
      documentSearchHeaders: (state) => state.search.documentSearchHeaders,
    }),
    ...mapGetters({
      getDocumentColumnFilter: "search/getDocumentColumnFilter",
    }),
    headers() {
      const selectedValues = new Set(this.documentSearchHeaders || []);

      const headers = [
        {
          text: this.$t("search.fileName"),
          value: "fileName",
          type: "document",
          width: 400,
          class: "header header-text",
        },
        {
          value: "agendaid",
          text: this.$t("search.agendaName"),
          type: "document",
          class: "header header-text",
          width: 150,
        },
        {
          value: "meetingid",
          text: this.$t("search.meetingName"),
          type: "document",
          width: 150,
          class: "header header-text",
        },
        {
          value: "directoryid",
          text: this.$t("search.directoryName"),
          type: "document",
          class: "header header-text",
        },
        {
          value: "metadata_storage_last_modified",
          text: this.$t("search.modifiedDate"),
          type: "document",
          class: "header header-text",
          width: 150,
        },
        {
          value: "metadata_author",
          text: this.$t("search.modifiedBy"),
          type: "document",
          class: "header header-text",
        },
        {
          value: "username",
          text: this.$t("search.uploader"),
          type: "document",
          class: "header header-text",
        },
      ];
      if (
        !this.documentSearchHeaders ||
        this.documentSearchHeaders.length === 0
      ) {
        return headers;
      }
      return headers.filter((header) => selectedValues.has(header.value));
    },
    documents() {
      return this.items;
    },
    tenantId() {
      return sessionStorage.getItem("tenantId");
    },
  },
  methods: {
    ...mapActions({
      downloadDocumentAPI: "document/downloadDocument",
    }),
    currentDateTime,
    formatBytes,
    formatDate,
    getAgendaName,
    getMeetingName,
    onFileDownload,
  },
};

async function onFileDownload(file) {
  const params = {
    documentId: file.id,
    filename: file.fileName,
  };
  const res = await this.downloadDocumentAPI(params);
  var contentType = res.headers["content-type"];
  var blob = new Blob([res.data], { type: contentType });
  var url = window.URL.createObjectURL(blob);
  openDocument(url, file.fileName);
}
function getAgendaName(id) {
  const agenda = this.agendaListData.find((a) => a.id === id);
  if (agenda) {
    return agenda.agendaName;
  }
  return null;
}

function getMeetingName(id) {
  const meeting = this.meetingListData.find((a) => a.id === id);
  if (meeting) {
    return meeting.meetingName;
  }
  return null;
}
</script>

<style>
</style>